import {
    Box,
    Button,
    CardHeader,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { BETA_KEYS, COOKIES, FEATURE_FLAGS, HIDE_ONBOARDING_FOR_PATHS, LOCAL_STORAGE } from '@/constants';
import Cookies from 'js-cookie';
import { useSession } from '@/providers/session-provider';
import { NetProfileAuthsDocument } from '@/graphql/generated/net-profile-auths.generated';
import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { NetCard, NetCardLoading } from '@/components/social-profile/NetCard';
import { NetsDocument } from '@/graphql/generated/fetch-net-types.generated';
import { visibleNets } from '@/utils/profiles/visible-nets';
import { HowToRegRounded, ShareRounded } from '@mui/icons-material';
import {
    DashListType,
    DashSourceCode,
    NetProfileAuthStatusCount,
    TeamPermission,
} from '@/graphql/generated/types.generated';
import { DashsDocument } from '@/graphql/generated/dashs.generated';
import { Formik } from 'formik';
import PhoneTextField from '../common/PhoneTextField';
import yup from '@/libs/yup';
import parsePhoneNumberFromString, { parseIncompletePhoneNumber } from 'libphonenumber-js/max';
import { Trans, useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { UpdateUserPhoneNumberDocument } from '@/graphql/generated/update-user-phone-number.generated';
import { UpdateUserInfoDocument } from '@/graphql/generated/update-user-info.generated';
import { UpdateWorkspaceInfoDocument } from '@/graphql/generated/update-workspace-info.generated';
import { useTeamPermissions } from '@/providers/team-permissions-provider';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { useAndieChatControls } from '@/providers/andie-chat-provider';
import posthog from 'posthog-js';
import { POSTHOG_ENABLED } from '@/providers/posthog-provider';
import { useAndie } from '@/providers/andie-provider';
import { useEffect, useState } from 'react';
import toggleBeta from '@/utils/beta/toggle-beta';
import {
    WorkspacesResumeDocument,
    WorkspacesResumeQuery,
} from '@/graphql/generated/workspaces-resumes.generated';
import { BrandsDocument } from '@/graphql/generated/brands.generated';
import { isBrandPricingWorkspace } from '@/utils/users-path-filter';
import { SwitchWorkspaceDocument } from '@/graphql/generated/switch-workspace.generated';
import { sendMessage as sendMessageLegacy } from '@/legacy/components/LegacyIframe';
import { sendMessage as sendMessageOldLegacy } from '@/legacy/components/OldLegacyIframe';
import { HasFailedPaymentDocument } from '@/graphql/generated/has-payment-failed.generated';
import ConnectProfileSkipSurvey from './ConnectProfileSkipSurvey';
import IcpStep, { ICP_QUESTIONS } from './IcpStep';
import OnboardingGoalStep from './OnboardingGoalStep';
import { GOAL_AGENT, GOAL_CONTEXT, GOAL_HANDOFF, type OnboardingGoal } from './onboarding-goals';

const CALENDAR_PATH = '/posts/calendar';
// '/main' is Home, not Insights: the Insights area is '/dashboards' (navigation.dashboards).
// The Andie hand-off has always landed on Home and is left as it was, but the no-profile fallback
// goes to Insights, which is what the user asked for when they picked "analyse my results".
const HOME_PATH = '/main';
const INSIGHTS_PATH = '/dashboards';

// Connected Instagram profiles only. The public source is a scraped profile the workspace does not
// own, so a demo built on it is not the thing the user is being shown how to get.
const INSTAGRAM_DASH_SOURCES: DashSourceCode[] = [DashSourceCode.Instagram, DashSourceCode.InstagramV2];

interface formikValues {
    connectedNetCode: string;
    name: string;
    phone: string;
    detailsFilled: boolean;
    icpManagesFor: string;
    icpAccounts: string;
    icpSocialTeam: string;
    icpCurrentSetup: string;
    icpFilled: boolean;
}

const FinishOnboardingDialog = ({
    appInitialLoadRefetch,
    isTeamMember,
    workspacesData,
}: {
    appInitialLoadRefetch: () => void;
    isTeamMember: boolean;
    workspacesData: WorkspacesResumeQuery | undefined;
}) => {
    const { me, workspace, refetch } = useSession();

    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { teamPermissions } = useTeamPermissions();
    const navigate = useNavigate();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const apolloClient = useApolloClient();

    const { setAgentId: setAndieAgentId, startNewConversation } = useAndieChatControls();
    const { setOpen: setAndieOpen, processingFirstMessage } = useAndie();

    // Andie drives steps 4-5 (goal + autonomous run). Without it we finish right after the connect step.
    const hasAndie = POSTHOG_ENABLED && posthog.isFeatureEnabled(FEATURE_FLAGS.COMPOSE_BETA);

    // Which action opened the skip survey. It carries the user's intent, and the dismiss button
    // means different things depending on it: opened from the Skip link the user already asked to
    // leave, so closing it either way skips; opened from a backdrop click they asked for nothing,
    // so "not now" declines the question and leaves them on the step.
    const [surveyTrigger, setSurveyTrigger] = useState<'skip' | 'exit' | null>(null);
    const surveyOpen = surveyTrigger !== null;
    // Two different suppressions, because dismissing is not answering.
    // `surveySeen` stops the survey popping up *unprompted* again (backdrop click). Waving one
    // away should not mean we keep interrupting.
    // `surveyAnswered` is the stronger one: the user actually told us why, so pressing Skip later
    // goes straight through. Without the split, dismissing an unsolicited popup burned our only
    // chance to ask at the moment that matters, which is the explicit Skip.
    const [surveySeen, setSurveySeen] = useState(false);
    const [surveyAnswered, setSurveyAnswered] = useState(false);
    // Connecting a profile is optional now; the user can advance without one. `connectionSkipped`
    // tracks that explicit choice, while connecting a profile also moves the flow forward.
    const [connectionSkipped, setConnectionSkipped] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState<OnboardingGoal | ''>('');
    const [finished, setFinished] = useState(false);
    const [icpIndex, setIcpIndex] = useState(0);

    useEffect(() => {
        posthog.capture('onboarding.registration');
    }, []);

    const [switchWorkspaceMutation, { loading: loadingSwitchWorkspace }] =
        useMutation(SwitchWorkspaceDocument);

    const [updatePersonalInfo] = useMutation(UpdateUserInfoDocument, {
        onError: ({ message }) => enqueueSnackbar(message),
    });
    const [updateUserPhoneNumber] = useMutation(UpdateUserPhoneNumberDocument, {
        onError: (error) => enqueueSnackbar(t(error.message)),
    });
    const [updateWorkspaceInfo] = useMutation(UpdateWorkspaceInfoDocument, {
        onError: (error) => enqueueSnackbar(t(error.message)),
    });

    const isBrand = isBrandPricingWorkspace(workspace);

    const { data: brandsData } = useQuery(BrandsDocument, {
        variables: { take: 1, skip: 0, includeEmpty: true },
        skip: !isBrand,
    });

    const brandPublicId: number | undefined = isBrand
        ? (brandsData?.groups?.items?.[0]?.publicId ?? undefined)
        : undefined;

    const { data: nets, loading: loadingNets } = useQuery(NetsDocument, {
        variables: {
            onlyPublish: true,
        },
        notifyOnNetworkStatusChange: true,
    });

    const {
        data: profilesData,
        loading: loadingProfiles,
        refetch: getProfiles,
    } = useQuery(NetProfileAuthsDocument, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        variables: {
            cache: false,
        },
    });

    // Whether the workspace ends onboarding with a social profile attached. It decides where the
    // goal step hands off: Andie only has something to work with once a profile exists.
    const hasProfiles = Boolean(profilesData?.netProfileAuths?.total);

    // Fetched on demand, only for the goal that needs it. Demo dashboards are global rather than
    // workspace-scoped (workspaceId null, isDemo true), so a brand new workspace already has them.
    // `take` is capped at 20 by PaginatedArgs on the server; asking for more fails validation and
    // the whole query errors, which would silently drop the user on the insights area instead of a
    // demo dashboard. The demo set is small and global, so one page covers it.
    const [fetchDemoDashboards] = useLazyQuery(DashsDocument, {
        variables: { type: DashListType.Demos, take: 20 },
        fetchPolicy: 'network-only',
    });

    const updateInfo = async (values: formikValues) => {
        await updatePersonalInfo({
            variables: { name: values.name || '' },
        });

        if (values.phone) {
            await updateUserPhoneNumber({
                variables: { phoneNumber: values.phone || '' },
            });
        }

        if (teamPermissions.includes(TeamPermission.Owner) && workspace) {
            await updateWorkspaceInfo({
                variables: { name: values.name || '' },
            });
        }
    };

    // Persist that onboarding is done so it doesn't reappear — even when the user skipped connecting a
    // profile (the legacy "has profiles" check alone would keep showing it). Mirrors the ICP flag.
    // `path` says which finish the user took: the Andie goal step or the legacy submit.
    const markFinished = (path: 'andie' | 'legacy') => {
        // The only record of a finished onboarding outside this browser's localStorage. PostHog
        // workflows exit on it, since `onboarding.registration` fires when the dialog opens.
        if (POSTHOG_ENABLED) {
            posthog.capture('onboarding.completed', {
                path,
                connected: hasProfiles,
                team_member: Boolean(isTeamMember),
            });
        }
        try {
            localStorage.setItem(LOCAL_STORAGE.ONBOARDING_FINISHED, '1');
            localStorage.removeItem(LOCAL_STORAGE.ONBOARDING_ICP_FILLED);
        } catch {
            // Ignore storage errors (e.g. blocked/disabled localStorage)
        }
        Cookies.remove(COOKIES.ONBOARDING_ID);
        // TODO remove this when the betas are stable
        toggleBeta(BETA_KEYS.COMPOSE_BETA, true);
        toggleBeta(BETA_KEYS.REPORTS_BETA, true);
    };

    const switchToWorkspaceId = (id: string, redirectPath: string) => {
        switchWorkspaceMutation({
            variables: { id },
            onCompleted: () => {
                navigate(redirectPath);
                apolloClient.resetStore().then(() => {
                    sendMessageLegacy({ type: 'reload' });
                    sendMessageOldLegacy({ type: 'reload' });
                });
            },
            onError: (e) => enqueueSnackbar(t(e.message)),
            refetchQueries: [HasFailedPaymentDocument],
        });
    };

    // Team members live in a different workspace than the one onboarding ran in; hop them over before
    // landing on the target path. Returns true when a workspace switch was triggered.
    const teamMemberRedirect = (path: string) => {
        if (isTeamMember) {
            const otherWorkspaceId =
                workspacesData?.workspaces.items?.find((w) => w.id !== workspace?.id)?.id || '';
            if (otherWorkspaceId) {
                switchToWorkspaceId(otherWorkspaceId, path);
                return true;
            }
        }

        navigate(path);
        return false;
    };

    // A profile is connected, so Andie has branding, analytics and somewhere to save a post.
    // This is the original hand-off, unchanged: navigate to the goal area and start the run.
    const handoffToAndie = (goal: OnboardingGoal) => {
        const target = GOAL_HANDOFF[goal];
        const path = target === 'insights' ? HOME_PATH : CALENDAR_PATH;
        refetch(() => {
            appInitialLoadRefetch();
            const switched = teamMemberRedirect(path);
            if (hasAndie && !switched) {
                setAndieAgentId(GOAL_AGENT[goal]);
                processingFirstMessage.current = true;
                setTimeout(() => {
                    setAndieOpen(true);
                    startNewConversation(
                        `${t(`onboarding.in-app.goal.${goal}.prompt`)}\n<context-message>\n${GOAL_CONTEXT[goal]}\n</context-message>`,
                    );
                }, 100);
            }
        });
    };

    // No profile connected. Andie cannot regenerate branding, cannot build a real dashboard and
    // cannot save a post, so it is not opened at all: the user is sent to a part of the product
    // that works without a connection instead.
    const handoffWithoutProfile = async (goal: OnboardingGoal) => {
        if (goal === 'unsure') {
            // Home already carries the get-started checklist, which is the right landing for
            // someone who has not decided. Andie cannot help here: no profile means no branding,
            // no analytics and nowhere to save a post.
            refetch(() => {
                appInitialLoadRefetch();
                teamMemberRedirect(HOME_PATH);
            });
            return;
        }

        if (goal === 'create') {
            // The calendar is empty and nothing can be saved to it without a profile, so the
            // planning area explains itself on arrival instead of showing a blank month.
            refetch(() => {
                appInitialLoadRefetch();
                teamMemberRedirect(`${CALENDAR_PATH}?handoff=onboarding`);
            });
            return;
        }

        // Instagram is by far the most used network, so prefer an Instagram demo over whatever
        // happens to come back first. Falling back to the insights area keeps the user moving if
        // no demo exists at all.
        let demoDashboardId: string | undefined;
        try {
            const { data } = await fetchDemoDashboards();
            const demos = data?.dashs.items || [];
            const instagramDemo = demos.find((demo) =>
                demo.sources?.some((source) => INSTAGRAM_DASH_SOURCES.includes(source.code)),
            );
            demoDashboardId = (instagramDemo || demos[0])?.id;
        } catch {
            // Ignore: the fallback below covers it.
        }

        refetch(() => {
            appInitialLoadRefetch();
            // `handoff` tells the dashboard page to explain that this is a demo and offer to build
            // the same thing on the user's own account.
            teamMemberRedirect(
                demoDashboardId ? `/dashboards/${demoDashboardId}?handoff=onboarding` : INSIGHTS_PATH,
            );
        });
    };

    // Goal selection finishes onboarding immediately, then hands off based on whether the user
    // arrives with a profile or without one.
    const handleGoalSelected = (goal: OnboardingGoal) => {
        setSelectedGoal(goal);
        markFinished('andie');
        setFinished(true);
        if (POSTHOG_ENABLED) {
            posthog.capture('onboarding.goal-selected', { goal, connected: hasProfiles });
        }
        if (hasProfiles) {
            handoffToAndie(goal);
        } else {
            void handoffWithoutProfile(goal);
        }
    };

    return (
        <Formik<formikValues>
            validateOnMount
            initialValues={{
                connectedNetCode: 'instagram',
                name: me?.name || '',
                phone: me?.phoneNumber || '',
                detailsFilled: Boolean(
                    (me?.name && me?.phoneNumber && !isTeamMember) || (me?.name && isTeamMember),
                ),
                icpManagesFor: '',
                icpAccounts: '',
                icpSocialTeam: '',
                icpCurrentSetup: '',
                icpFilled: (() => {
                    try {
                        return Boolean(localStorage.getItem(LOCAL_STORAGE.ONBOARDING_ICP_FILLED));
                    } catch {
                        return false;
                    }
                })(),
            }}
            validationSchema={yup.object({
                connectedNetCode: yup.string(),
                name: yup.string().required(),
                phone: !isTeamMember
                    ? yup
                          .string()
                          .trim()
                          .required()
                          .test('is-valid-phone', t('str.phone-number.invalid'), (value) => {
                              const phoneNumber = parseIncompletePhoneNumber(value);
                              const phoneNumberParsed = parsePhoneNumberFromString(phoneNumber);
                              return Boolean(phoneNumberParsed?.isValid());
                          })
                    : yup.string().nullable(),
            })}
            onSubmit={async () => {
                // Legacy finish path (team members / no Andie): just finalize and land on the calendar.
                markFinished('legacy');
                setFinished(true);
                refetch(() => {
                    appInitialLoadRefetch();
                    teamMemberRedirect(CALENDAR_PATH);
                });
            }}
        >
            {({ values, handleChange, handleSubmit, isSubmitting, isValid, setValues, errors, touched }) => {
                const connectionDone = connectionSkipped || hasProfiles;
                // Only wait on the profiles query once the flow actually depends on it (connect step
                // onward) — the name/ICP steps shouldn't be held back, and this avoids a brief "connect"
                // flash for returning users whose profiles haven't loaded yet.
                const initialLoading =
                    loadingProfiles &&
                    !profilesData &&
                    values.detailsFilled &&
                    (isTeamMember || values.icpFilled);

                const activeStep = !values.detailsFilled
                    ? 0
                    : isTeamMember
                      ? 5
                      : !values.icpFilled
                        ? 1
                        : !connectionDone
                          ? 2
                          : !hasAndie
                            ? 5
                            : 3;

                const stepBars = [0, 1, 2, 3];

                return (
                    <>
                        <Dialog
                            open={!finished}
                            onClose={() => {
                                if (activeStep === 2 && !connectionSkipped && !surveySeen) {
                                    setSurveyTrigger('exit');
                                }
                            }}
                            fullWidth
                            maxWidth="sm"
                            fullScreen={isMobile}
                            slotProps={{
                                paper: {
                                    sx: {
                                        borderRadius: isMobile ? 0 : 4,
                                    },
                                },
                            }}
                        >
                            <form onSubmit={handleSubmit}>
                                <DialogTitle sx={{ p: 0 }}>
                                    <Stack gap={2} px={{ xs: 2, sm: 3 }} py={2}>
                                        <CardHeader
                                            title={<Trans i18nKey="onboarding.in-app.title" />}
                                            subheader={<Trans i18nKey="onboarding.in-app.subtitle" />}
                                            slotProps={{
                                                title: { variant: 'h4', color: 'textPrimary' },
                                                subheader: { variant: 'body1', color: 'textSecondary' },
                                            }}
                                            sx={{ p: 0 }}
                                        />
                                        <Stepper
                                            activeStep={activeStep}
                                            alternativeLabel
                                            connector={null}
                                            sx={{ gap: 1 }}
                                        >
                                            {stepBars.map((step) => (
                                                <Step key={step} sx={{ p: 0 }}>
                                                    <StepLabel
                                                        sx={{
                                                            '& > .MuiStepLabel-iconContainer': {
                                                                borderRadius: 2,
                                                                width: '100%',
                                                                height: '8px',
                                                                bgcolor: 'lightGrey',
                                                                '&.Mui-active, &.Mui-completed': {
                                                                    bgcolor: 'primary.light',
                                                                },
                                                                '& > svg': { display: 'none' },
                                                            },
                                                        }}
                                                    />
                                                </Step>
                                            ))}
                                        </Stepper>
                                    </Stack>
                                    <Divider />
                                </DialogTitle>
                                <DialogContent>
                                    {initialLoading && (
                                        <Stack alignItems="center" py={5}>
                                            <CircularProgress />
                                        </Stack>
                                    )}
                                    {!initialLoading && activeStep === 0 && (
                                        <Stack>
                                            <Stack alignItems="center" py={5} px={2}>
                                                <Stack alignItems="center" textAlign="center">
                                                    <Stack
                                                        sx={{
                                                            aspectRatio: 1,
                                                            p: 2,
                                                            borderRadius: '50%',
                                                            color: 'success.main',
                                                            bgcolor: 'success.light',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            mb: 1,
                                                        }}
                                                    >
                                                        <HowToRegRounded fontSize="large" />
                                                    </Stack>
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontSize="large"
                                                        gutterBottom
                                                    >
                                                        <Trans i18nKey="onboarding.in-app.final-details-title" />
                                                    </Typography>
                                                    <Typography variant="body1" gutterBottom>
                                                        <Trans i18nKey="onboarding.in-app.final-details-description" />
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                justifyContent="center"
                                                gap={2}
                                                flexWrap="wrap"
                                            >
                                                <TextField
                                                    label={t('onboarding.in-app.name-label')}
                                                    placeholder={t('onboarding.in-app.name-placeholder')}
                                                    variant="outlined"
                                                    fullWidth
                                                    required
                                                    value={values.name}
                                                    onChange={handleChange}
                                                    name="name"
                                                    error={!!errors.name && touched.name}
                                                />
                                                {!isTeamMember && (
                                                    <PhoneTextField
                                                        label={t('onboarding.in-app.phone-label')}
                                                        placeholder={t('onboarding.in-app.phone-placeholder')}
                                                        variant="outlined"
                                                        fullWidth
                                                        required
                                                        value={values.phone}
                                                        onChange={(phone) => {
                                                            handleChange({
                                                                target: { name: 'phone', value: phone },
                                                            });
                                                        }}
                                                        error={!!errors.phone && touched.phone}
                                                        name="phone"
                                                    />
                                                )}
                                            </Stack>
                                        </Stack>
                                    )}
                                    {!initialLoading && activeStep === 1 && (
                                        <IcpStep
                                            values={values}
                                            index={icpIndex}
                                            onAnswer={(field, value) => {
                                                setValues((prev) => ({ ...prev, [field]: value }));
                                                // Commit each answer as it's given: someone who drops
                                                // out halfway still lands in PostHog with the answers
                                                // they did give, and the gap shows where they stopped.
                                                if (POSTHOG_ENABLED) {
                                                    const question = ICP_QUESTIONS.find(
                                                        (icpQuestion) => icpQuestion.field === field,
                                                    );
                                                    if (question) {
                                                        posthog.setPersonProperties({
                                                            [question.personProperty]: value,
                                                        });
                                                    }
                                                }
                                            }}
                                            onAdvance={() => {
                                                if (icpIndex < ICP_QUESTIONS.length - 1) {
                                                    setIcpIndex((prev) => prev + 1);
                                                    return;
                                                }
                                                try {
                                                    localStorage.setItem(
                                                        LOCAL_STORAGE.ONBOARDING_ICP_FILLED,
                                                        '1',
                                                    );
                                                } catch {
                                                    // Ignore storage errors (e.g. blocked/disabled localStorage)
                                                }
                                                setValues((prev) => ({ ...prev, icpFilled: true }));
                                            }}
                                        />
                                    )}
                                    {!initialLoading && activeStep === 2 && (
                                        <Stack>
                                            <Stack alignItems="center" py={3} px={2}>
                                                <Stack alignItems="center" textAlign="center">
                                                    <Stack
                                                        sx={{
                                                            aspectRatio: 1,
                                                            p: 2,
                                                            borderRadius: '50%',
                                                            color: 'primary.contrastText',
                                                            bgcolor: 'primary.light',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            mb: 1,
                                                        }}
                                                    >
                                                        <ShareRounded fontSize="large" />
                                                    </Stack>
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontSize="large"
                                                        gutterBottom
                                                    >
                                                        <Trans i18nKey="onboarding.in-app.connect-profile-title" />
                                                    </Typography>
                                                    <Typography variant="body1" gutterBottom>
                                                        <Trans i18nKey="onboarding.in-app.connect-profile-benefits" />
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                            <Stack gap={1} px={2}>
                                                {loadingNets || loadingProfiles ? (
                                                    <>
                                                        <NetCardLoading layout="row" />
                                                        <NetCardLoading layout="row" />
                                                        <NetCardLoading layout="row" />
                                                    </>
                                                ) : (
                                                    visibleNets(nets?.nets).map((net) => (
                                                        <Box key={net.id} width={1}>
                                                            <NetCard
                                                                net={net}
                                                                brandId={brandPublicId}
                                                                surface="onboarding"
                                                                layout="row"
                                                                onConnect={() => {
                                                                    setValues({
                                                                        ...values,
                                                                        connectedNetCode: net.code,
                                                                    });

                                                                    getProfiles({ cache: false });
                                                                }}
                                                            />
                                                        </Box>
                                                    ))
                                                )}
                                            </Stack>
                                        </Stack>
                                    )}
                                    {!initialLoading && activeStep === 3 && (
                                        <OnboardingGoalStep
                                            value={selectedGoal}
                                            onSelect={handleGoalSelected}
                                            hasProfile={hasProfiles}
                                        />
                                    )}
                                    {!initialLoading && activeStep === 5 && (
                                        <Stack>
                                            <Stack alignItems="center" py={5} px={2}>
                                                <Stack alignItems="center" textAlign="center">
                                                    <Stack
                                                        sx={{
                                                            aspectRatio: 1,
                                                            p: 2,
                                                            borderRadius: '50%',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            mb: 1,
                                                            fontSize: 44,
                                                        }}
                                                    >
                                                        🎉
                                                    </Stack>
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontSize="large"
                                                        gutterBottom
                                                    >
                                                        {t('onboarding.in-app.finish.title')}
                                                    </Typography>
                                                    <Typography variant="body1" gutterBottom>
                                                        {t(
                                                            'onboarding.in-app.finish.description-no-briefings',
                                                        )}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </Stack>
                                    )}
                                </DialogContent>
                                {[0, 1, 2, 5].includes(activeStep) ? (
                                    <DialogActions>
                                        <Stack width={1} px={2} py={1} direction="row" justifyContent="right">
                                            {activeStep === 2 && (
                                                <Button
                                                    variant="text"
                                                    color="secondary"
                                                    sx={{ mr: 'auto' }}
                                                    onClick={() => {
                                                        if (connectionSkipped) return;
                                                        // Already told us why: skip without
                                                        // asking the same question twice. A mere
                                                        // dismissal does not count, so we still
                                                        // ask here, which is the moment we most
                                                        // want the answer.
                                                        if (surveyAnswered) {
                                                            setConnectionSkipped(true);
                                                            return;
                                                        }
                                                        setSurveyTrigger('skip');
                                                    }}
                                                >
                                                    {t('onboarding.in-app.connect-skip')}
                                                </Button>
                                            )}
                                            {activeStep === 0 && (
                                                <Button
                                                    variant="contained"
                                                    disabled={!isValid}
                                                    size="large"
                                                    onClick={async () => {
                                                        await updateInfo(values);
                                                        setValues((prev) => ({
                                                            ...prev,
                                                            detailsFilled: true,
                                                        }));
                                                    }}
                                                >
                                                    {t('str.action.progress.continue')}
                                                </Button>
                                            )}
                                            {activeStep === 1 && (
                                                <>
                                                    <Button
                                                        variant="text"
                                                        color="secondary"
                                                        sx={{ mr: 'auto' }}
                                                        disabled={icpIndex === 0}
                                                        data-id="onboarding-icp-back"
                                                        onClick={() =>
                                                            setIcpIndex((prev) => Math.max(prev - 1, 0))
                                                        }
                                                    >
                                                        {t('str.action.escape.back')}
                                                    </Button>
                                                    {/* Answering advances by itself, so the only thing
                                                        left to show here is how far along the step is. */}
                                                    <Stack
                                                        direction="row"
                                                        gap={1}
                                                        alignItems="center"
                                                        role="progressbar"
                                                        aria-valuenow={icpIndex + 1}
                                                        aria-valuemin={1}
                                                        aria-valuemax={ICP_QUESTIONS.length}
                                                        data-id="onboarding-icp-progress"
                                                    >
                                                        {ICP_QUESTIONS.map((question, questionIndex) => (
                                                            <Box
                                                                key={question.field}
                                                                sx={{
                                                                    // The current question stretches
                                                                    // into a pill, so you can see where
                                                                    // you are without counting dots.
                                                                    width:
                                                                        questionIndex === icpIndex ? 20 : 8,
                                                                    height: 8,
                                                                    borderRadius: 4,
                                                                    bgcolor:
                                                                        questionIndex <= icpIndex
                                                                            ? 'primary.main'
                                                                            : 'text.disabled',
                                                                    transition:
                                                                        'width 180ms ease-out, background-color 180ms ease-out',
                                                                    '@media (prefers-reduced-motion: reduce)':
                                                                        { transition: 'none' },
                                                                }}
                                                            />
                                                        ))}
                                                    </Stack>
                                                </>
                                            )}
                                            {activeStep === 5 && (
                                                <LoadingButton
                                                    loading={isSubmitting || loadingSwitchWorkspace}
                                                    variant="contained"
                                                    disabled={!isValid}
                                                    size="large"
                                                    type="submit"
                                                >
                                                    {t('str.action.progress.continue')}
                                                </LoadingButton>
                                            )}
                                        </Stack>
                                    </DialogActions>
                                ) : null}
                            </form>
                        </Dialog>
                        <ConnectProfileSkipSurvey
                            open={surveyOpen}
                            trigger={surveyTrigger}
                            onClose={(answered) => {
                                // Only a survey the user opened by asking to skip advances the flow.
                                const askedToSkip = surveyTrigger === 'skip';
                                setSurveyTrigger(null);
                                setSurveySeen(true);
                                if (answered) setSurveyAnswered(true);
                                if (askedToSkip) setConnectionSkipped(true);
                            }}
                        />
                    </>
                );
            }}
        </Formik>
    );
};

const isOnboardingFinished = () => {
    try {
        return Boolean(localStorage.getItem(LOCAL_STORAGE.ONBOARDING_FINISHED));
    } catch {
        return false;
    }
};

const FinishOnboarding = ({
    appInitialLoad,
}: {
    appInitialLoad: {
        refetch: () => void;
        statusLevelCount: NetProfileAuthStatusCount;
    };
}) => {
    const { me } = useSession();
    const { pathname } = useLocation();

    const { data: workspacesData, loading: workspaceLoading } = useQuery(WorkspacesResumeDocument, {
        notifyOnNetworkStatusChange: true,
    });

    if (HIDE_ONBOARDING_FOR_PATHS.includes(pathname)) return null;

    const isTeamMember = workspacesData?.workspaces.total && workspacesData?.workspaces.total > 1;
    const notTeamMemberWithoutProfiles =
        appInitialLoad.statusLevelCount.statusLevels.filter((level) => level.count > 0).length === 0 &&
        !isTeamMember;

    if (!workspaceLoading) {
        // Show onboarding if the user didn't finish registration (no name). Users without a connected
        // profile also see it — unless they already completed the (now skippable) onboarding flow.
        if (!me?.name || (notTeamMemberWithoutProfiles && !isOnboardingFinished())) {
            return (
                <FinishOnboardingDialog
                    appInitialLoadRefetch={appInitialLoad.refetch}
                    isTeamMember={!!isTeamMember}
                    workspacesData={workspacesData}
                />
            );
        }
    }

    return null;
};

export default FinishOnboarding;
